// ─────────────────────────────────────────────────────────────────
// Sehat AI — Main App Component
// ─────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import config from './config';

// Components
import Header from './components/Header';
import ChatBubble, { TypingBubble } from './components/ChatBubble';
import DoctorCard from './components/DoctorCard';
import QuickSymptoms from './components/QuickSymptoms';
import InputBar from './components/InputBar';
import VoiceOverlay from './components/VoiceOverlay';

// Hooks
import { useLocation } from './hooks/useLocation';
import { useVoiceInput } from './hooks/useVoiceInput';
import { useVoiceConversation } from './hooks/useVoiceConversation';

// Utils
import { callClaude } from './utils/claudeApi';
import { fetchDoctorsFromPlaces, geocodeCity } from './utils/placesApi';
import { speakIndian, stopSpeaking } from './utils/indianVoice';
import { extractMedicines } from './utils/medicineExtractor';

// Data
import { detectSpecialty, SPECIALTY_ICONS, SPECIALTY_LABELS } from './data/specialties';
import { getMockDoctors } from './data/mockDoctors';

// ── Location extraction helpers (module-level = stable, no useCallback dep) ──
const CITIES = [
  'Delhi','New Delhi','Mumbai','Bombay','Bangalore','Bengaluru','Chennai','Madras',
  'Kolkata','Calcutta','Hyderabad','Pune','Ahmedabad','Jaipur','Surat','Lucknow',
  'Kanpur','Nagpur','Patna','Indore','Thane','Bhopal','Chandigarh','Noida',
  'Gurgaon','Gurugram','Faridabad','Meerut','Agra','Varanasi','Banaras','Nashik',
  'Aurangabad','Vadodara','Baroda','Rajkot','Amritsar','Ludhiana','Coimbatore',
  'Madurai','Kochi','Cochin','Visakhapatnam','Vizag','Vijayawada','Thiruvananthapuram',
  'Trivandrum','Mysore','Mysuru','Hubli','Mangalore','Udaipur','Jodhpur','Ajmer',
  'Bikaner','Kota','Jalandhar','Patiala','Dehradun','Haridwar','Rishikesh',
  'Shimla','Jammu','Srinagar','Ranchi','Bhubaneswar','Guwahati','Raipur',
];

function extractCityFromText(text) {
  // Strategy 1: pattern "X mein/me doctor/hospital/batao" — catches any town name
  const patternMatch = text.match(
    /([a-zA-Z\u0900-\u097F][\w\s\u0900-\u097F]{1,30}?)\s+(?:me|mein|main|में)\s+(?:doctor|specialist|hospital|clinic|batao|bata|dhundo|dhundho|dikhao)/i
  );
  if (patternMatch) {
    const place = patternMatch[1].trim();
    if (place.length >= 3) return place;
  }
  // Strategy 2: known major city list
  const lower = text.toLowerCase();
  for (const c of CITIES) {
    if (lower.includes(c.toLowerCase())) return c;
  }
  return null;
}

export default function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [lang, setLang] = useState('en');

  const loc = useLocation();

  // When location is denied, user can mention a city in chat.
  // State drives UI; ref is read inside async callbacks to avoid stale closures.
  const [manualLoc, setManualLoc] = useState(null); // { lat, lng, city }
  const manualLocRef = useRef(null);

  const bottomRef = useRef(null);
  const greetedRef = useRef(false);
  // Always-fresh ref to messages — prevents stale closures in sendMessage
  const messagesRef = useRef([]);
  messagesRef.current = messages;
  // Latest found doctors — passed to Claude so it can compare and recommend
  const doctorContextRef = useRef('');

  // Push-to-talk (text mode)
  const { isListening, start: startMic, stop: stopMic } = useVoiceInput({
    lang,
    onResult: (transcript) => setInput(transcript),
  });

  // Forwarding ref — lets useVoiceConversation call sendMessage
  // even though sendMessage is defined later with useCallback
  const sendMessageRef = useRef(null);

  // Hands-free voice conversation loop
  const conv = useVoiceConversation({
    lang,
    onSend: useCallback((text) => sendMessageRef.current?.(text), []),
    isSpeaking,
  });

  // Latest doctor + tablet results for the voice overlay
  const overlayData = useMemo(() => {
    const reversed = [...messages].reverse().filter(m => m.role === 'assistant');
    const docMsg = reversed.find(m => Array.isArray(m.doctors) && m.doctors.length > 0);
    const tabMsg = reversed.find(m => Array.isArray(m.tablets) && m.tablets.length > 0);
    return {
      doctors:  docMsg?.doctors  || [],
      specialty: docMsg?.specialty,
      tablets:  tabMsg?.tablets  || [],
    };
  }, [messages]);

  // ── Initial greeting — waits for real location before firing ──
  useEffect(() => {
    if (greetedRef.current) return;
    if (!loc.ready) return;

    greetedRef.current = true;
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Subah ki shubhkamnaen! 🌅' : hour < 17 ? 'Namaskar! 🙏' : 'Shubh sandhya! 🌆';

    const intro = loc.denied
      ? `${greeting} Main Sehat AI hoon — aapki apni health companion! 😊\n\nApni takleef batayein — main aapki madad karungi. Agar kisi khaas sheher mein doctor dhundhna ho toh bas bata dein, main wahan dhundh lungi! 🎤`
      : `${greeting} Main Sehat AI hoon — aapki apni health companion! 😊\n\nApni takleef batayein, main ${loc.city} mein aapke paas ke doctors dhundhugi. Tablet ya ghar ka nuskha bhi puchhna ho toh zaroor batayein! Hindi mein bhi bol sakte hain, ya mic se baat kar sakte hain! 🎤`;

    setMessages([
      { role: 'system', content: loc.city ? `📍 ${loc.city}` : '📍 Location not available' },
      { role: 'assistant', content: intro },
    ]);
    if (voiceOn) {
      speakIndian(intro, lang, () => setIsSpeaking(true), () => setIsSpeaking(false));
    }
  }, [loc.ready, loc.denied, loc.city]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto scroll to bottom ─────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send message ──────────────────────────────────────────────
  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    setInput('');

    // Use the ref so we always get the latest messages even across re-renders
    const snapshot = messagesRef.current;
    const userMsg = { role: 'user', content: trimmed };
    const updatedMessages = [...snapshot, userMsg];
    setMessages(updatedMessages);
    setLoading(true);

    try {
      // ── Resolve search location ──────────────────────────────────
      // ALWAYS check if the user mentioned a new city in this message.
      // This lets them switch locations: "Delhi mein doctor" → "Khetri Nagar mein doctor"
      let searchCoords = null;
      const mentionedCity = extractCityFromText(trimmed);

      if (mentionedCity) {
        // User explicitly named a place — geocode it and use it (replaces old location)
        try {
          const coords = await geocodeCity(mentionedCity);
          if (coords) {
            manualLocRef.current = coords;
            setManualLoc(coords);
            searchCoords = coords;
          }
        } catch {}
      }

      // No city in message — fall back to GPS or previously extracted location
      if (!searchCoords) {
        if (!loc.denied && loc.lat && loc.lng) {
          searchCoords = { lat: loc.lat, lng: loc.lng, city: loc.city };
        } else if (manualLocRef.current) {
          searchCoords = manualLocRef.current;
        }
      }

      const effectiveCity = searchCoords?.city || null;
      const reply = await callClaude(updatedMessages, effectiveCity, doctorContextRef.current);

      // Only suppress a new doctor search when:
      //   (a) doctors have already been found in this session, AND
      //   (b) the message looks like a follow-up (fees, comparison, medication etc.)
      const hasExistingDoctors = !!doctorContextRef.current;
      const isFollowUp = hasExistingDoctors &&
        /\b(konsa|which|better|best|kitne|fee|fees|price|paise|paisa|rupee|rupay|tablet|medicine|dawa|dawai|recommend|compare|cost|cheap|sasta|mehnga)\b/i.test(trimmed);
      const specialty = isFollowUp
        ? null
        : (detectSpecialty(trimmed) || detectSpecialty(reply));

      // Give each message a stable id so we can update it with doctors later
      const msgId = Date.now();
      const aiMsg = {
        id: msgId,
        role: 'assistant',
        content: reply,
        sp: specialty || undefined,
        specialty: specialty || undefined,
        searching: !!specialty,
        doctors: null,
        tablets: extractMedicines(reply),
      };
      setMessages(prev => [...prev, aiMsg]);

      if (voiceOn) {
        speakIndian(reply, lang, () => setIsSpeaking(true), () => setIsSpeaking(false));
      }

      if (specialty) {
        setTimeout(async () => {
          // No location at all — skip search entirely, don't show wrong city data
          if (!searchCoords) {
            setMessages(prev => prev.map(m =>
              m.id === msgId ? { ...m, searching: false, doctors: [] } : m
            ));
            return;
          }

          let docs = null;
          if (!config.isDemoMode) {
            try {
              docs = await fetchDoctorsFromPlaces(specialty, searchCoords.lat, searchCoords.lng);
            } catch (err) {
              console.warn('Places API failed, using mock data:', err.message);
            }
          }
          const finalDocs = docs?.length ? docs : getMockDoctors(specialty);

          // Build context string for Claude so it can compare and recommend
          doctorContextRef.current =
            `Found ${finalDocs.length} ${specialty} results near ${searchCoords.city || 'your area'}:\n` +
            finalDocs.map((d, i) =>
              `${i + 1}. ${d.name} | ⭐ ${d.rating}/5 (${d.reviews?.toLocaleString() ?? 0} reviews) | 📍 ${d.distance} | ` +
              `${d.open === true ? 'Open Now' : d.open === false ? 'Closed' : 'Hours unknown'} | ${d.address}`
            ).join('\n');

          // Attach doctors to the specific AI message — keeps them in history
          setMessages(prev => prev.map(m =>
            m.id === msgId ? { ...m, searching: false, doctors: finalDocs } : m
          ));
        }, 700);
      }
    } catch (err) {
      console.error('Claude API error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Network issue aa gayi ji. Thodi der baad dobara try karein 🙏',
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, voiceOn, lang, loc]); // removed `messages` — using messagesRef instead

  // Keep forwarding ref current so voice conversation can always call sendMessage
  sendMessageRef.current = sendMessage;

  // Stop TTS + end voice conversation mode
  const handleEndConv = useCallback(() => {
    stopSpeaking();       // stop ElevenLabs / Web Speech immediately
    setIsSpeaking(false); // reset UI state
    conv.disable();       // exit hands-free mode
  }, [conv]);

  const handleStopSpeaking = () => {
    stopSpeaking();
    setIsSpeaking(false);
  };

  const handleToggleVoice = () => {
    stopSpeaking();
    setIsSpeaking(false);
    setVoiceOn(v => !v);
  };

  const hasUserMessages = messages.some(m => m.role === 'user');

  return (
    <div style={{ minHeight: '100vh', background: '#FFF7F3', fontFamily: "'Nunito',sans-serif", display: 'flex', flexDirection: 'column' }}>

      <Header
        city={loc.city || manualLoc?.city}
        locationDenied={loc.denied}
        onRetryLocation={loc.retry}
        lang={lang}
        voiceOn={voiceOn}
        onToggleLang={() => setLang(l => l === 'en' ? 'hi' : 'en')}
        onToggleVoice={handleToggleVoice}
      />

      {/* Demo mode banner */}
{/* demo mode banner removed — not user-facing */}

      {/* Location loading screen */}
      {!loc.ready && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 32 }}>
          <div style={{ fontSize: 44 }}>📍</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#FF6B35' }}>Aapki location dhundh rahi hoon...</div>
          <div style={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>
            Behtar results ke liye location allow karein 🙏
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {[0,1,2].map(i => (
              <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF6B35', opacity: 0.7, animation: `dot 1.2s ease ${i * 0.2}s infinite` }} />
            ))}
          </div>
        </div>
      )}

      {/* Main chat area */}
      {loc.ready && <div style={{ flex: 1, maxWidth: 700, width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 16px 10px' }}>

          {/* Quick symptom chips (only before first message) */}
          {!hasUserMessages && (
            <QuickSymptoms onSelect={sendMessage} />
          )}

          {/* Messages — doctors are embedded in each message so they persist in history */}
          {messages.map((msg, i) => (
            <React.Fragment key={msg.id ?? i}>
              <ChatBubble msg={msg} />

              {/* Searching spinner attached to this specific message */}
              {msg.searching && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#FFF7F5', borderRadius: 14, padding: '12px 16px', border: '1.5px solid #FFD8CC', marginBottom: 12 }}>
                  <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    {[5, 10, 7, 14, 9].map((h, j) => (
                      <div key={j} style={{ width: 3, height: h, background: '#FF6B35', borderRadius: 2, animation: `sw 0.65s ease ${j * 0.08}s infinite alternate` }} />
                    ))}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#FF6B35' }}>Doctors dhundh rahe hain...</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                      {`${loc.city || manualLoc?.city || 'Aapke aas paas'} mein dhundh rahi hoon…`}
                    </div>
                  </div>
                </div>
              )}

              {/* Doctor results attached to this specific message */}
              {!msg.searching && msg.doctors?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: '#FFF7F5', borderRadius: 12, padding: '10px 15px', border: '1.5px solid #FFD8CC', marginBottom: 12 }}>
                    <span style={{ fontSize: 22 }}>{SPECIALTY_ICONS[msg.specialty] || '🩺'}</span>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 900, color: '#FF6B35' }}>
                        {msg.doctors.length} {SPECIALTY_LABELS[msg.specialty] || msg.specialty} found near {loc.city || manualLoc?.city || 'your area'}
                      </div>
                      <div style={{ fontSize: 10.5, color: '#9CA3AF' }}>
                        📍 Aapke najdeek
                      </div>
                    </div>
                  </div>
                  {msg.doctors.map((doc, j) => (
                    <DoctorCard key={j} doc={doc} idx={j} specialty={msg.specialty} />
                  ))}
                  <div style={{ fontSize: 10.5, color: '#9CA3AF', textAlign: 'center', padding: '8px 14px', background: '#F9FAFB', borderRadius: 10, marginTop: 6, lineHeight: 1.5 }}>
                    ⚕️ Sehat AI provides guidance only. Always consult a registered medical professional for diagnosis and treatment.
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}

          {/* Typing indicator */}
          {loading && <TypingBubble />}

          <div ref={bottomRef} />
        </div>

        {/* Input bar */}
        <InputBar
          input={input}
          onChange={setInput}
          onSend={() => sendMessage()}
          onMicStart={startMic}
          onMicStop={stopMic}
          isListening={isListening}
          isSpeaking={isSpeaking}
          onStopSpeaking={handleStopSpeaking}
          lang={lang}
          loading={loading}
          convMode={conv.active}
          convListening={conv.listening}
          convInterim={conv.interim}
          convSupported={conv.supported}
          onStartConv={conv.enable}
          onEndConv={handleEndConv}
        />
      </div>}

      {/* Full-page voice overlay — position:fixed, sits above everything */}
      <VoiceOverlay
        active={conv.active}
        listening={conv.listening}
        isSpeaking={isSpeaking}
        loading={loading}
        interim={conv.interim}
        onEnd={handleEndConv}
        doctors={overlayData.doctors}
        specialty={overlayData.specialty}
        tablets={overlayData.tablets}
        isSendPending={conv.isSendPending}
        onSendNow={conv.sendNow}
      />

      {/* Global CSS animations */}
      <style>{`
        @keyframes dot {
          0%,80%,100% { transform:translateY(0); opacity:0.4; }
          40% { transform:translateY(-7px); opacity:1; }
        }
        @keyframes popUp {
          from { opacity:0; transform:translateY(16px) scale(0.97); }
          to   { opacity:1; transform:translateY(0) scale(1); }
        }
        @keyframes pulse {
          0%,100% { opacity:0.5; transform:scale(0.9); }
          50% { opacity:1; transform:scale(1.1); }
        }
        @keyframes sw {
          from { transform:scaleY(0.35); }
          to   { transform:scaleY(1.7); }
        }
        @keyframes spin {
          from { transform:rotate(0deg); }
          to   { transform:rotate(360deg); }
        }
        @keyframes slideUp {
          from { transform:translateY(100%); opacity:0; }
          to   { transform:translateY(0);    opacity:1; }
        }
        button:active { transform:scale(0.95); }
        textarea { scrollbar-width:none; }
        textarea::-webkit-scrollbar { display:none; }
        ::-webkit-scrollbar { width:4px; }
        ::-webkit-scrollbar-thumb { background:#FFD8CC; border-radius:4px; }
      `}</style>
    </div>
  );
}
